import React, { useRef, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import ButtonComponent from './ButtonComponent';
import { VisuallyHidden } from '@chakra-ui/react';

const DrawingPadComponent = ({ prompt, ariaLabel, onConfirm, isDisabled, readOnly, handleButtonAction, isTouchDevice, isMouseDevice }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const primaryColor =
    getComputedStyle(document.documentElement)
      .getPropertyValue('--sutter-sync-purple')
      .trim() || '#5F1B93';
  const lineWidth = 4;

  const startDrawing = (e) => {
    const ctx = canvasRef.current.getContext('2d');
    const { offsetX, offsetY } = getEventPosition(e);
    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const { offsetX, offsetY } = getEventPosition(e);
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
  };

  const getEventPosition = (e) => {
    if (e.touches) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: touch.clientX - rect.left,
        offsetY: touch.clientY - rect.top,
      };
    }
    return {
      offsetX: e.nativeEvent.offsetX,
      offsetY: e.nativeEvent.offsetY,
    };
  };

  return (
    <Box 
    direction="column"
    height="100%"
    justify="flex-start" mt={4}>
      {/* Prompt */}
      <Text
        mb={4}
        color="var(--color-dusky-teal, #000)"
        fontFamily={`var(--body-md-min-reg-font-family, inherit)`}
        fontWeight={`var(--body-md-min-reg-font-weight, bold)`}
        fontSize={`var(--body-md-min-reg-font-size, inherit)`}
        lineHeight={`var(--body-md-min-reg-line-height, normal)`}
        letterSpacing={`var(--body-md-min-reg-letter-spacing, normal)`}
      >
        {prompt}
      </Text>

      {/* Canvas */}
      <Box
        width="100%"
        height="145px"
        border="3px solid var(--color-dusky-teal, #203433)"
        borderRadius="md"
        bg="white"
        overflow="hidden"
        position="relative"
        mb={8}
        aria-label={ariaLabel}
        role="img"
        tabIndex={isDisabled ? -1 : 0}
        aria-describedby="drawing-pad-description"
      >
        <canvas
          ref={canvasRef}
          width={document.documentElement.clientWidth - 32}
          height={145}
          style={{
            display: 'block',
            cursor: isDisabled ? 'not-allowed' : 'crosshair',
          }}
          onMouseDown={!isDisabled ? startDrawing : undefined}
          onMouseMove={!isDisabled ? draw : undefined}
          onMouseUp={!isDisabled ? stopDrawing : undefined}
          onMouseLeave={!isDisabled ? stopDrawing : undefined}
          onTouchStart={!isDisabled ? startDrawing : undefined}
          onTouchMove={!isDisabled ? draw : undefined}
          onTouchEnd={!isDisabled ? stopDrawing : undefined}
        ></canvas>
      </Box>

      <VisuallyHidden id="drawing-pad-description">
        Use your mouse or touch to draw in the box above. Press "Next" when
        you're done.
      </VisuallyHidden>
      {/* Action Buttons */}
      {!readOnly && (
        <Flex justify="space-between" gap={4} mt={6}>
        <ButtonComponent
                  label='Back'
                  variant='outline-half'
                  ariaLabel='Go back'
                  tooltip='Click to go back'
                  onClick={() => handleButtonAction('back', {})}
                  mb={'8px'}
                  isTouchDevice={isTouchDevice}
                  isMouseDevice={isMouseDevice}
                />
          <ButtonComponent
            label="Next"
            variant={isDisabled || !hasDrawn ? 'disabled-half' : 'solid-half'}
            isDisabled={isDisabled || !hasDrawn}
            aria-label={
              isDisabled || !hasDrawn
                ? 'Button disabled. Complete the drawing to proceed.'
                : 'Confirm your drawing and proceed to the next step'
            }
            onClick={() => onConfirm && onConfirm()}
            mb={'8px'}
            isTouchDevice={isTouchDevice}
            isMouseDevice={isMouseDevice}
          />
        </Flex>
      )}
    </Box>
  );
};

export default DrawingPadComponent;
